import { useForm } from '@refinedev/antd';
import { useEffect, useState } from 'react';

interface UseInlineSectionEditOptions<T> {
  id: number;
  record: T;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  refetch: () => Promise<any>;
  successMessage?: string;
}

/**
 * Generaliza el patrón de edición inline de una sección del Show: un useForm
 * propio (sin fetch, usa los datos ya cargados por el Show), resync del form
 * cuando se sale del modo edición, y wrapping de onFinish/loading.
 */
const useInlineSectionEdit = <T extends Record<string, any>>({
  id,
  record,
  isEditing,
  setIsEditing,
  refetch,
  successMessage,
}: UseInlineSectionEditOptions<T>) => {
  const [isSaving, setIsSaving] = useState(false);
  const { formProps } = useForm({
    redirect: false,
    action: 'edit',
    resource: 'service_orders',
    id,
    queryOptions: { enabled: false },
    successNotification: successMessage ? { message: successMessage, type: 'success' } : undefined,
    onMutationSuccess: () => {
      void refetch().then(() => setIsSaving(false));
    },
    onMutationError: () => setIsSaving(false),
  });

  useEffect(() => {
    if (!isEditing) {
      formProps?.form?.setFieldsValue(record);
    }
  }, [formProps?.form, isEditing, record]);

  const startEditing = () => {
    formProps?.form?.setFieldsValue(record);
    setIsEditing(true);
  };

  const handleFinish = (values: any) => {
    setIsSaving(true);
    formProps?.onFinish?.(values);
    setIsEditing(false);
  };

  return { formProps, isSaving, startEditing, handleFinish };
};

export default useInlineSectionEdit;
